/**
 * LinkInBioPublicPage — öffentliche „Link-in-Bio"-Mini-Seite (mobil zuerst).
 *
 * Erreichbar unter /p/:slug OHNE Login (vor dem Auth-Gate gerendert). Holt nur
 * die explizit öffentlichen Felder von GET /api/link-in-bio/public/:slug — es gibt
 * keinen Workspace-/Nutzerbezug. Links öffnen extern (noopener/nofollow).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MEDIA_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Defense-in-depth: auch wenn das Backend bereits http(s) erzwingt, rendern wir hier
// niemals eine nicht-http(s)-URL als Link (kein javascript:/data:-Vektor) und klemmen
// die Theme-Farbe auf einen gültigen Hex-Wert.
const isHttpUrl = (u) => typeof u === "string" && /^https?:\/\//i.test(u.trim());

// Ein hochgeladenes Profilbild liegt unter dem Medien-Pfad des Backends statt unter
// http(s). Die Prüfung ist am vollständigen Präfix verankert — „beginnt mit /" wäre
// unsicher, weil protokollrelative Werte wie //fremde-seite.tld/x durchrutschen würden.
// Form gespiegelt aus link_in_bio/models.py (UPLOADED_MEDIA_URL_RE) — inkl. Token,
// ohne das der Medien-Endpunkt ohnehin 404 liefert.
const isUploadedImagePath = (u) =>
  typeof u === "string" &&
  /^\/api\/media\/file\/[0-9a-f]{24}\?t=[A-Za-z0-9_-]{16,128}$/.test(u.trim());
const isImageSrc = (u) => isHttpUrl(u) || isUploadedImagePath(u);
const imageSrc = (u) => {
  const raw = typeof u === "string" ? u.trim() : "";
  return isUploadedImagePath(raw) ? `${MEDIA_BASE}${raw}` : raw;
};
const safeTheme = (c) => (typeof c === "string" && /^#[0-9a-fA-F]{6}$/.test(c.trim()) ? c.trim() : "#EA580C");

export default function LinkInBioPublicPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, profile: null, notFound: false });

  useEffect(() => {
    let active = true;
    setState({ loading: true, profile: null, notFound: false });
    axios
      .get(`${API}/link-in-bio/public/${encodeURIComponent(slug)}`)
      .then((res) => {
        if (active) setState({ loading: false, profile: res.data, notFound: false });
      })
      .catch((err) => {
        if (!active) return;
        setState({ loading: false, profile: null, notFound: err?.response?.status === 404 });
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="animate-spin text-stone-400" size={28} />
      </div>
    );
  }

  if (state.notFound || !state.profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 px-6 text-center">
        <div className="text-5xl mb-4">🦊</div>
        <h1 className="text-xl font-semibold text-stone-800">Profil nicht gefunden</h1>
        <p className="text-stone-500 mt-2 max-w-sm">
          Diese Seite existiert nicht (mehr) oder ist nicht veröffentlicht.
        </p>
      </div>
    );
  }

  const p = state.profile;
  const theme = safeTheme(p.theme_color);
  const initials = (p.title || "?").trim().slice(0, 1).toUpperCase();
  const links = (p.links || []).filter((l) => isHttpUrl(l.url));
  const avatarOk = isImageSrc(p.avatar_url);

  return (
    <div
      className="min-h-screen bg-stone-50"
      style={{ background: `linear-gradient(180deg, ${theme}14 0%, #fafaf9 260px)` }}
    >
      <div className="max-w-md mx-auto px-5 pt-12 pb-10">
        <div className="flex flex-col items-center text-center">
          {avatarOk ? (
            <img
              src={imageSrc(p.avatar_url)}
              alt=""
              className="w-24 h-24 rounded-full object-cover shadow-md ring-4 ring-white"
            />
          ) : (
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-md ring-4 ring-white"
              style={{ backgroundColor: theme }}
            >
              {initials}
            </div>
          )}
          <h1 className="mt-4 text-2xl font-bold text-stone-900 break-words">{p.title}</h1>
          {p.description && (
            <p className="mt-2 text-stone-600 leading-relaxed whitespace-pre-line">{p.description}</p>
          )}
        </div>

        <div className="mt-8 space-y-3">
          {links.map((l, i) => (
            <a
              key={`${l.url}-${i}`}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-between gap-3 w-full px-5 py-4 rounded-2xl bg-white border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all font-medium text-stone-800"
              style={{ borderColor: `${theme}33` }}
            >
              <span className="truncate">{l.label}</span>
              <ExternalLink size={16} className="flex-shrink-0 text-stone-400" />
            </a>
          ))}
          {links.length === 0 && (
            <p className="text-center text-sm text-stone-400">Noch keine Links hinterlegt.</p>
          )}
        </div>

        <div className="mt-10 text-center">
          <a
            href="https://foerderfuchs-bildung.de"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-600"
          >
            <span>🦊</span> Erstellt mit Förderfuchs
          </a>
        </div>
      </div>
    </div>
  );
}
